/**
 * Search — the «Поиск» tab: one field, and what it finds.
 *
 * Task 10. `spec.md` stories 52–56 and «Поиск»: one field over the title, the billing, the
 * riddim, the tags and the year; Latin finds Cyrillic and back; results as the listener types;
 * an unfinished word is found; videos as a group of their own. The server does the finding
 * (`apps/api/src/routes/search.ts`); `store/search.ts` decides when to ask it.
 *
 * ## What the screen shows, top to bottom
 *
 * ```
 * Поиск
 * [ поле                        × ]
 * Исполнители   (Sizzla · 40 треков) (…)     ← подсказки: открывают раздел
 * Riddim        (Sleng Teng · 12 треков)
 * Треки         строка, строка, …            ← касание играет, «⋮» открывает карточку
 * Видео         строка, строка, …            ← только когда видео нашлись
 * ```
 *
 * Suggestions come first although the specification lists tracks first: they are one line
 * each, and «Sizzla · 40 треков» above forty rows of Sizzla is the shortcut the listener was
 * looking for when they typed the name. They are chips, not rows, so five of them still leave
 * the tracks on the first screen of a small phone.
 *
 * Before anything is typed, the screen under the field is the neon sign «Что ищем?» over the hint
 * (task 68; the dimmed logo since task 41): an empty tab that says what it is for. «Ничего не
 * нашлось» and a failure show no sign — it means «here it comes», not «something is wrong».
 *
 * ## A tap on a suggestion leaves the tab
 *
 * An artist or a riddim opens in its section of «Треки» (`spec.md`: «исполнители и riddim'ы
 * — как разделы-подсказки»), with «Назад» leading to that section. The shell switches the tab;
 * this screen only says which list (`onOpenCollection`). The query and its results stay in
 * their slice, so coming back to «Поиск» finds them where they were.
 *
 * ## A tap on a track plays it
 *
 * As in «Треки»: the row plays, the queue is the results from that row down, and «⋮» opens
 * the card with the artist, the riddim and the tags. The results are at most fifty rows, so
 * they are drawn as they are — the virtual list is for the catalogue's thousands.
 *
 * ## A tap on a clip opens «Видео» (task 13)
 *
 * The clip goes into the tab's frame and starts there (`onOpenVideo`; the shell switches the
 * tab, as for an artist). The row is the tab's own (`components/VideoRow.tsx`), so a clip
 * looks the same in both places; an mp4 is not a control until its player exists (task 14).
 * The group appears only when something was found in it, after the tracks.
 */

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ScreenHead } from '../components/ScreenHead';
import { SearchBox } from '../components/SearchBox';
import { TrackCard } from '../components/TrackCard';
import { ListState } from '../components/TrackListView';
import { TrackRow } from '../components/TrackRow';
import { VideoRow } from '../components/VideoRow';
import { player } from '../engine/player';
import type { SearchAnswer, TrackItem, TrackQuery, VideoItem } from '../lib/api';
import { trackCount } from '../lib/format';
import type { Collection } from '../store/library';
import { listKey, useLibrary } from '../store/library';
import { useSearch } from '../store/search';
import '../styles/tracks.css';
import '../styles/search.css';
import '../styles/video.css';

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ──────────────────────────────────────────────────────────────────────────── */

export const SEARCH_TITLE = 'Поиск';
export const FIELD_LABEL = 'Что найти';
/** Short enough for 375 pixels; the hint under the field names every source, tags included. */
export const FIELD_PLACEHOLDER = 'Исполнитель, трек, riddim, год';
export const CLEAR_LABEL = 'Очистить поиск';
/** Task 72: shorter, as on the approved picture — the examples under it say the rest. */
export const HINT_TEXT = 'Трек, исполнитель, riddim, тег или год — латиницей или кириллицей.';
/** Task 72: the heading over the examples under an empty field. */
export const EXAMPLES_LABEL = 'Например';
export const SEARCHING_TEXT = 'Ищу…';
export const NOTHING_TIP = 'Попробуйте начало слова или другое написание.';
export const STALE_FAILED_TEXT = 'Результаты не обновились.';
export const MORE_TRACKS_TEXT =
  'Показаны пятьдесят лучших совпадений. Добавьте слово, чтобы сузить поиск.';
export const RETRY_LABEL = 'Ещё раз';
export const ARTISTS_LABEL = 'Исполнители';
export const RIDDIMS_LABEL = 'Riddim';
export const TRACKS_LABEL = 'Треки';
export const VIDEOS_LABEL = 'Видео';

/** What an empty answer says, with the query in it so the listener sees what was searched. */
export function nothingFound(text: string): string {
  return `По запросу «${text.trim()}» ничего не нашлось.`;
}

/** Task 72: the heading of a group of results with how many it holds — «Треки · 8», «50+». */
export function groupHeading(label: string, count: number, more = false): string {
  return `${label} · ${String(count)}${more ? '+' : ''}`;
}

/**
 * Task 72: what to suggest under an empty field — up to five words the catalogue has, never a
 * list written by hand that would go stale: the two biggest artists and the two biggest riddims
 * (the server answers both sections biggest first), and the year most tracks known here are
 * from. In the picture's order: artist, riddim, year, artist, riddim.
 */
export function searchExamples(
  artists: readonly { readonly name: string }[],
  riddims: readonly { readonly name: string }[],
  tracks: readonly { readonly year: number | null }[],
): string[] {
  const counts = new Map<number, number>();
  for (const track of tracks) {
    if (track.year !== null) counts.set(track.year, (counts.get(track.year) ?? 0) + 1);
  }
  let year: number | undefined;
  for (const [candidate, count] of counts) {
    const best = year === undefined ? 0 : (counts.get(year) ?? 0);
    if (count > best || (count === best && year !== undefined && candidate > year))
      year = candidate;
  }
  const words = [
    artists[0]?.name,
    riddims[0]?.name,
    year === undefined ? undefined : String(year),
    artists[1]?.name,
    riddims[1]?.name,
  ];
  return [...new Set(words.filter((word): word is string => word !== undefined && word !== ''))];
}

/** The text of a suggestion: the name and the length of the list it opens. */
export function suggestionLabel(name: string, count: number): string {
  return `${name} · ${trackCount(count)}`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pieces
 * ──────────────────────────────────────────────────────────────────────────── */

function isEmpty(answer: SearchAnswer): boolean {
  return (
    answer.tracks.length === 0 &&
    answer.videos.length === 0 &&
    answer.artists.length === 0 &&
    answer.riddims.length === 0
  );
}

/** One group of suggestions: a heading and a line of chips. Nothing at all when empty. */
function Suggestions({
  label,
  entries,
  onOpen,
}: {
  readonly label: string;
  readonly entries: readonly {
    readonly id: string;
    readonly text: string;
    readonly collection: Collection;
  }[];
  readonly onOpen: (collection: Collection) => void;
}): JSX.Element | null {
  if (entries.length === 0) return null;
  return (
    <section className="searchGroup" aria-label={label}>
      <h2 className="searchGroupTitle">{label}</h2>
      <div className="chips">
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="chip chipGold"
            onClick={() => {
              onOpen(entry.collection);
            }}
          >
            {entry.text}
          </button>
        ))}
      </div>
    </section>
  );
}

/** Task 72: «Например» and the chips under an empty field; a tap puts the word in and searches. */
function Examples({ onPick }: { readonly onPick: (word: string) => void }): JSX.Element | null {
  const artists = useLibrary((s) => s.artists.items);
  const riddims = useLibrary((s) => s.riddims.items);
  const known = useLibrary((s) => s.lists[listKey(ALL_TRACKS)]?.items);

  useEffect(() => {
    void useLibrary.getState().loadArtists();
    void useLibrary.getState().loadRiddims();
  }, []);

  const words = searchExamples(artists, riddims, known ?? []);
  if (words.length === 0) return null;
  return (
    <section className="searchExamples" aria-label={EXAMPLES_LABEL}>
      <h2 className="searchGroupTitle">{EXAMPLES_LABEL}</h2>
      <div className="chips">
        {words.map((word) => (
          <button
            key={word}
            type="button"
            className="chip"
            onClick={() => {
              onPick(word);
            }}
          >
            {word}
          </button>
        ))}
      </div>
    </section>
  );
}

/** «Все треки», the list the year of the examples is counted over. */
const ALL_TRACKS: TrackQuery = Object.freeze({ kind: 'all' });

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SearchScreenProps {
  /** Opens an artist's or a riddim's list in «Треки». The shell switches the tab. */
  readonly onOpenCollection: (collection: Collection) => void;
  /** Task 13: plays a clip in «Видео». The shell switches the tab. */
  readonly onOpenVideo: (video: VideoItem) => void;
}

export function SearchScreen({ onOpenCollection, onOpenVideo }: SearchScreenProps): JSX.Element {
  const text = useSearch((s) => s.text);
  const query = useSearch((s) => s.query);
  const answer = useSearch((s) => s.answer);
  const answeredText = useSearch((s) => s.answeredText);
  const status = useSearch((s) => s.status);
  const failure = useSearch((s) => s.failure);
  const setText = useSearch((s) => s.setText);
  const retry = useSearch((s) => s.retry);

  const field = useRef<HTMLInputElement | null>(null);
  const [card, setCard] = useState<TrackItem | undefined>(undefined);

  // The tab is opened to search: an empty field takes the focus. A field that already holds a
  // query is a listener coming back to their results, and a keyboard over them is in the way.
  useEffect(() => {
    if (useSearch.getState().text === '') field.current?.focus();
  }, []);

  const tracks = answer?.tracks;
  const play = useCallback(
    (track: TrackItem): void => {
      player().playFrom(tracks ?? [track], track);
    },
    [tracks],
  );
  const enqueue = useCallback((track: TrackItem): boolean => player().enqueue(track), []);
  const closeCard = useCallback((): void => {
    setCard(undefined);
  }, []);
  const openFromCard = useCallback(
    (collection: Collection): void => {
      setCard(undefined);
      onOpenCollection(collection);
    },
    [onOpenCollection],
  );

  let body: JSX.Element;
  if (query === '') {
    // Task 68: nothing asked yet is a place that will fill — the neon sign over the hint; task 72:
    // and words to try under it.
    body = (
      <ListState text={HINT_TEXT} sign="poisk">
        <Examples onPick={setText} />
      </ListState>
    );
  } else if (answer === undefined) {
    body =
      status === 'failed' ? (
        <div className="listState">
          <p className="stateText">{failure?.message ?? STALE_FAILED_TEXT}</p>
          <div className="actions">
            <button type="button" className="button" onClick={retry}>
              {RETRY_LABEL}
            </button>
          </div>
        </div>
      ) : (
        <p className="searchNote">{SEARCHING_TEXT}</p>
      );
  } else {
    body = (
      <div className="searchResults" aria-busy={status === 'loading'}>
        {status === 'failed' && (
          <p className="searchNote">
            {STALE_FAILED_TEXT}
            <button type="button" className="linkButton" onClick={retry}>
              {RETRY_LABEL}
            </button>
          </p>
        )}

        {isEmpty(answer) && (
          <div className="listState">
            <p className="stateText">{nothingFound(answeredText)}</p>
            <p className="searchNote">{NOTHING_TIP}</p>
          </div>
        )}

        <Suggestions
          label={ARTISTS_LABEL}
          entries={answer.artists.map((artist) => ({
            id: `a:${artist.key}`,
            text: suggestionLabel(artist.name, artist.trackCount),
            collection: { kind: 'artist', key: artist.key, name: artist.name },
          }))}
          onOpen={onOpenCollection}
        />
        <Suggestions
          label={RIDDIMS_LABEL}
          entries={answer.riddims.map((riddim) => ({
            id: `r:${String(riddim.id)}`,
            text: suggestionLabel(riddim.name, riddim.trackCount),
            collection: { kind: 'riddim', id: riddim.id, name: riddim.name },
          }))}
          onOpen={onOpenCollection}
        />

        {answer.tracks.length > 0 && (
          <section className="searchGroup" aria-label={TRACKS_LABEL}>
            <h2 className="searchGroupTitle">
              {groupHeading(TRACKS_LABEL, answer.tracks.length, answer.moreTracks)}
            </h2>
            <ul className="searchList">
              {answer.tracks.map((track) => (
                <TrackRow
                  key={track.id}
                  track={track}
                  onPlay={play}
                  onDetails={setCard}
                  mark={answeredText}
                />
              ))}
            </ul>
            {answer.moreTracks && <p className="searchNote">{MORE_TRACKS_TEXT}</p>}
          </section>
        )}

        {answer.videos.length > 0 && (
          <section className="searchGroup" aria-label={VIDEOS_LABEL}>
            <h2 className="searchGroupTitle">{groupHeading(VIDEOS_LABEL, answer.videos.length)}</h2>
            <ul className="searchList">
              {answer.videos.map((video) => (
                <VideoRow key={video.id} item={video} onPlay={onOpenVideo} />
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="search">
      <header className="tracksHead">
        <ScreenHead title={SEARCH_TITLE} />
        <SearchBox
          value={text}
          onChange={setText}
          label={FIELD_LABEL}
          placeholder={FIELD_PLACEHOLDER}
          clearLabel={CLEAR_LABEL}
          fieldRef={field}
        />
      </header>

      {body}

      {card !== undefined && (
        <TrackCard
          track={card}
          onClose={closeCard}
          onOpenCollection={openFromCard}
          onPlay={play}
          onEnqueue={enqueue}
        />
      )}
    </div>
  );
}
